import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import { TicketPriority, TicketStatus } from 'helpers/Enums';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { LocalizationIds } from 'helpers/Localizations';

interface ICustomerTicketDetailsContext {
    ticket: ITicket
    isLoading: boolean
    isLoadingTicketMessages: boolean
    messagges: IMessage[]
    isDownloadingDocument: string | null
    showEditDialog: boolean

    downloadAttachment: (documentId: string) => Promise<void>
    saveMessage: (values: SaveMessage) => Promise<void>
    openEditDialogHandler: () => void
    closeEditDialogHandler: (reloadTicket: boolean) => Promise<void>
}

export interface ITicket {
    isReadOnly: boolean
    ticketId: string
    ticketNumber: string
    title: string
    status: TicketStatus
    category: string
    categoryId: string
    ticketSubCategoryId: string
    customerId: number
    customerName: string
    reporterUserName: string
    reporterUser: string
    assigneeUserName: string
    assigneeUser: string
    priority: TicketPriority
    orders: IOrder[]
    workOrders: IWorkOrder[]
    groups: string[]
}

interface IOrder {
    orderId: number
    orderNumber: string
    orderType: string
    orderTitle: string
    orderDate: moment.Moment
}

interface IWorkOrder {
    workOrderId: number
    workOrderNumber: string
    articleId: string
    articleModel: string
    articleVersion: string
    articleDescription: string
    orderId?: number
    orderNumber: string
    orderType: string
    orderTitle: string
    orderDate?: moment.Moment
}

export interface IMessage {
    messageId: string
    isReadOnly: boolean
    value: string
    username: string
    userFullName: string
    date: moment.Moment
    attachments: IAttachment[]
}

export interface IAttachment {
    fileId: string
    extension: string
    isImage: boolean
    isVideo: boolean
    fileName: string
    username: string
    date: moment.Moment
}


export let messageSaveSchema = yup.object({
    messageId: yup.string().default(null).nullable(),
    text: yup.string().default(''),
    attachments: yup.array(
        yup.object({
            fileId: yup.string().default(''),
            fileName: yup.string().default('')
        })
    ).default([])
});

type SaveMessage = yup.InferType<typeof messageSaveSchema>;


export let customerTicketDetailsContext = React.createContext({} as ICustomerTicketDetailsContext);

let { Provider } = customerTicketDetailsContext;

let CustomerTicketDetailsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { formatMessage } = useIntl();
    let { id } = useParams<{ id: string }>();
    let { enqueueSnackbar } = useSnackbar();

    let [isLoading, setLoading] = useState(true);
    let [ticket, setTicket] = useState<ITicket>({} as ITicket);

    let [isLoadingTicketMessages, setLoadingTicketMessages] = useState(false);
    let [messages, setMessages] = useState<IMessage[]>([]);

    let [isDownloadingDocument, setIsDownloadingDocument] = useState<string | null>(null);

    let [showEditDialog, setShowEditDialog] = useState<boolean>(false);

    useEffect(() => {
        getTicket();
        getTicketMessages();
    }, []);

    let getTicket = async (setIsLoading: boolean = true) => {
        try {
            if (setIsLoading === true) {
                setLoading(true);
            }

            let res = await axios.get('/api/CustomerTicket/GetTicket', { params: { ticketNumber: id } });

            setTicket(res.data.item);

        } catch (err) { }
        finally {
            if (setIsLoading === true) {
                setLoading(false);
            }
        }
    }

    let getTicketMessages = async () => {
        try {
            setLoadingTicketMessages(true);

            let res = await axios.get('/api/CustomerTicketMessage/GetTicketMessages', { params: { ticketNumber: id } })

            setMessages(res.data.items);

        } catch (err) { }
        finally {
            setLoadingTicketMessages(false);
        }
    }

    let saveMessage = async (values: SaveMessage) => {
        try {
            let request = {
                ticketId: ticket.ticketId,
                ...values
            }
            await axios.post('/api/CustomerTicketMessage/SaveTicketMessage', request);
            enqueueSnackbar(formatMessage({ id: LocalizationIds.MessageSaved }), { variant: 'success' });

        } catch (err) { }
        finally {
            setLoading(false);
            await getTicketMessages();
            if (ticket.status === TicketStatus.WaitingForCustomer)
                await getTicket(false);
        }
    }

    let downloadAttachment = async (documentId: string) => {
        try {
            setIsDownloadingDocument(documentId);
            let res = await axios.get('/api/CustomerTicket/DownloadTicketAttachmentDocument', { params: { documentId } });
            window.location.href = res.data;
            setIsDownloadingDocument(null);
        } catch (err) {
            enqueueSnackbar(formatMessage({ id: LocalizationIds.ERR_FILE_DOWNLOAD }), { variant: "error" });
            setIsDownloadingDocument(null);
        }
    }


    let openEditDialogHandler = () => {
        setShowEditDialog(true);
    }

    let closeEditDialogHandler = async (reloadTicket: boolean) => {
        setShowEditDialog(false);
        if (reloadTicket === true) {
            await getTicket(false);
        }
    }

    return (
        <Provider value={{
            ticket,
            isLoading,
            isLoadingTicketMessages,
            messagges: messages,
            isDownloadingDocument,
            showEditDialog,

            downloadAttachment,
            saveMessage,
            openEditDialogHandler,
            closeEditDialogHandler
        }}>
            {children}
        </Provider>
    );
}

export default CustomerTicketDetailsProvider;